import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AcceptAnalysisRequest,
  AnalysisResponse,
  CategoriesResponse,
  CreateMediaRequest,
  MediaResponse,
  ClubRole,
  ExerciseContentInput,
  ExerciseDetailResponse,
  ExerciseListQuery,
  ExerciseListResponse,
  ModerationDetailResponse,
  ModerationListResponse,
  SubmissionScope,
  CreateInvitationRequest,
  CreateOrganizationRequest,
  CreateOrganizationResponse,
  InvitationDetailsResponse,
  InvitationResponse,
  InvitationsResponse,
  MeResponse,
  MembersResponse,
  OrganizationResponse,
  OrganizationsResponse,
  PlatformOrganizationsResponse,
  PlatformUsersResponse,
  PasswordResetLinkResponse,
  SystemStatus,
  TrainingDetailResponse,
  TrainingInput,
  TrainingListResponse,
  TrainingScope,
} from '@train2volley/shared';
import { api } from './api';

export const keys = {
  me: ['me'] as const,
  organizations: ['organizations'] as const,
  members: (orgId: string) => ['organizations', orgId, 'members'] as const,
  invitations: (orgId: string) => ['organizations', orgId, 'invitations'] as const,
  invitation: (id: string) => ['invitation', id] as const,
  platformOrganizations: ['platform', 'organizations'] as const,
  platformUsers: (q: string) => ['platform', 'users', q] as const,
  platformStatus: ['platform', 'status'] as const,
  categories: ['categories'] as const,
  exercises: ['exercises'] as const,
  exerciseList: (q: ExerciseListQuery) => ['exercises', 'list', q] as const,
  exercise: (id: string) => ['exercises', 'detail', id] as const,
  moderation: ['moderation'] as const,
  submission: (id: string) => ['moderation', id] as const,
  media: (id: string) => ['media', id] as const,
  analysis: (id: string) => ['analysis', id] as const,
  trainings: ['trainings'] as const,
  trainingList: ['trainings', 'list'] as const,
  trainingListScope: (scope: TrainingScope) => ['trainings', 'list', scope] as const,
  training: (id: string) => ['trainings', 'detail', id] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: () => api<MeResponse>('/me') });

export const useOrganizations = () =>
  useQuery({
    queryKey: keys.organizations,
    queryFn: () => api<OrganizationsResponse>('/organizations'),
  });

/** De actieve club zoals de server hem bevestigt (null = persoonlijke modus). */
export function useActiveOrganization() {
  const query = useOrganizations();
  const active =
    query.data?.organizations.find((o) => o.id === query.data.activeOrganizationId) ?? null;
  return { ...query, active };
}

export const useMembers = (orgId: string) =>
  useQuery({
    queryKey: keys.members(orgId),
    queryFn: () => api<MembersResponse>(`/organizations/${orgId}/members`),
  });

export const useInvitations = (orgId: string, enabled: boolean) =>
  useQuery({
    queryKey: keys.invitations(orgId),
    queryFn: () => api<InvitationsResponse>(`/organizations/${orgId}/invitations`),
    enabled,
  });

export const useInvitationDetails = (id: string) =>
  useQuery({
    queryKey: keys.invitation(id),
    queryFn: () => api<InvitationDetailsResponse>(`/invitations/${id}`),
    retry: false,
  });

export const usePlatformOrganizations = (enabled: boolean) =>
  useQuery({
    queryKey: keys.platformOrganizations,
    queryFn: () => api<PlatformOrganizationsResponse>('/platform/organizations'),
    enabled,
  });

/** Clubcontext wisselen: alles wat van de club afhangt opnieuw ophalen. */
export const usePlatformUsers = (q: string) =>
  useQuery({
    queryKey: keys.platformUsers(q),
    queryFn: () =>
      api<PlatformUsersResponse>(`/platform/users${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    placeholderData: (prev) => prev,
  });

/** S6: systeemstatus; ververst elke 30 s zolang het tabblad open is. */
export const usePlatformStatus = () =>
  useQuery({
    queryKey: keys.platformStatus,
    queryFn: () => api<SystemStatus>('/platform/status'),
    refetchInterval: 30_000,
  });

/** D-034: eenmalige herstellink; het antwoord niet in de cache bewaren. */
export const useCreatePasswordResetLink = () =>
  useMutation({
    mutationFn: (userId: string) =>
      api<PasswordResetLinkResponse>(`/platform/users/${userId}/password-reset-link`, {
        method: 'POST',
      }),
  });

export function useSetActiveOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (organizationId: string | null) =>
      api<OrganizationsResponse>('/organizations/active', {
        method: 'PUT',
        body: { organizationId },
      }),
    onSuccess: async (data) => {
      qc.setQueryData(keys.organizations, data);
      await qc.invalidateQueries();
    },
  });
}

export function useCreateInvitation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateInvitationRequest) =>
      api<InvitationResponse>(`/organizations/${orgId}/invitations`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invitations(orgId) }),
  });
}

export function useCancelInvitation(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/organizations/${orgId}/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.invitations(orgId) }),
  });
}

export function useUpdateMemberRole(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: ClubRole }) =>
      api<void>(`/organizations/${orgId}/members/${memberId}`, { method: 'PATCH', body: { role } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.members(orgId) }),
  });
}

export function useRemoveMember(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) =>
      api<void>(`/organizations/${orgId}/members/${memberId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useRespondToInvitation(id: string) {
  const qc = useQueryClient();
  return {
    accept: useMutation({
      mutationFn: () => api<OrganizationResponse>(`/invitations/${id}/accept`, { method: 'POST' }),
      onSuccess: () => qc.invalidateQueries(),
    }),
    reject: useMutation({
      mutationFn: () => api<void>(`/invitations/${id}/reject`, { method: 'POST' }),
    }),
  };
}

export function useCreateOrganization() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOrganizationRequest) =>
      api<CreateOrganizationResponse>('/platform/organizations', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.platformOrganizations }),
  });
}

// --- Oefeningen (S2) -------------------------------------------------------------

export const useCategories = () =>
  useQuery({
    queryKey: keys.categories,
    queryFn: () => api<CategoriesResponse>('/categories'),
    staleTime: Infinity,
  });

function toSearch(q: ExerciseListQuery, cursor?: string) {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== '') params.set(k, String(v));
  if (cursor) params.set('cursor', cursor);
  return params.toString();
}

/** Catalogus met "Meer laden" via cursor-paging. */
export const useExerciseList = (q: ExerciseListQuery) =>
  useInfiniteQuery({
    queryKey: keys.exerciseList(q),
    queryFn: ({ pageParam }) => api<ExerciseListResponse>(`/exercises?${toSearch(q, pageParam)}`),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

/** Pollt elke 5 s zolang de video nog wordt verwerkt (D-032). */
export const useExercise = (id: string) =>
  useQuery({
    queryKey: keys.exercise(id),
    queryFn: () => api<ExerciseDetailResponse>(`/exercises/${id}`),
    refetchInterval: (q) => (q.state.data?.exercise.video?.status === 'PROCESSING' ? 5000 : false),
  });

function useExerciseMutation<TVars>(fn: (vars: TVars) => Promise<ExerciseDetailResponse | void>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (data) => {
      if (data) qc.setQueryData(keys.exercise(data.exercise.id), data);
      await qc.invalidateQueries({ queryKey: keys.exercises });
      await qc.invalidateQueries({ queryKey: keys.moderation });
    },
  });
}

export const useCreateExercise = () =>
  useExerciseMutation((body: ExerciseContentInput) =>
    api<ExerciseDetailResponse>('/exercises', { method: 'POST', body }),
  );

export const useUpdateExercise = (id: string) =>
  useExerciseMutation((body: ExerciseContentInput) =>
    api<ExerciseDetailResponse>(`/exercises/${id}`, { method: 'PATCH', body }),
  );

export const useSubmitExercise = (id: string) =>
  useExerciseMutation((body: { scope: SubmissionScope; includeVideo?: boolean }) =>
    api<ExerciseDetailResponse>(`/exercises/${id}/submit`, { method: 'POST', body }),
  );

export const useRemoveExerciseVideo = (id: string) =>
  useExerciseMutation(() => api<void>(`/exercises/${id}/video`, { method: 'DELETE' }));

export const useWithdrawSubmission = (id: string) =>
  useExerciseMutation((submissionId: string) =>
    api<void>(`/exercises/${id}/submissions/${submissionId}/withdraw`, { method: 'POST' }),
  );

export const useArchiveExercise = (id: string) =>
  useExerciseMutation(() => api<void>(`/exercises/${id}`, { method: 'DELETE' }));

// --- Moderatie -------------------------------------------------------------------

export const useModerationList = (enabled: boolean) =>
  useQuery({
    queryKey: keys.moderation,
    queryFn: () => api<ModerationListResponse>('/moderation/submissions'),
    enabled,
  });

export const useSubmissionForReview = (id: string) =>
  useQuery({
    queryKey: keys.submission(id),
    queryFn: () => api<ModerationDetailResponse>(`/moderation/submissions/${id}`),
    retry: false,
  });

export function useReviewSubmission(id: string) {
  const qc = useQueryClient();
  const done = async () => {
    await qc.invalidateQueries({ queryKey: keys.moderation });
    await qc.invalidateQueries({ queryKey: keys.exercises });
  };
  return {
    approve: useMutation({
      mutationFn: () => api<void>(`/moderation/submissions/${id}/approve`, { method: 'POST' }),
      onSuccess: done,
    }),
    reject: useMutation({
      mutationFn: (reason: string) =>
        api<void>(`/moderation/submissions/${id}/reject`, { method: 'POST', body: { reason } }),
      onSuccess: done,
    }),
  };
}

// --- Video en AI-analyse -----------------------------------------------------------

const RUNNING = new Set(['QUEUED', 'PREPARING', 'PROCESSING']);

export const createMediaIntent = (body: CreateMediaRequest) =>
  api<MediaResponse>('/media', { method: 'POST', body });

export const requestAnalysis = (mediaId: string) =>
  api<AnalysisResponse>(`/media/${mediaId}/analyze`, {
    method: 'POST',
    body: { privacyConfirmation: true },
  });

export const useMedia = (id: string | null) =>
  useQuery({
    queryKey: keys.media(id ?? ''),
    queryFn: () => api<MediaResponse>(`/media/${id}`),
    enabled: id !== null,
  });

/** Pollt elke 2 s zolang de analyse loopt; stopt bij een eindstatus. */
export const useAnalysis = (id: string | null) =>
  useQuery({
    queryKey: keys.analysis(id ?? ''),
    queryFn: () => api<AnalysisResponse>(`/analyses/${id}`),
    enabled: id !== null,
    refetchInterval: (q) =>
      q.state.data && !RUNNING.has(q.state.data.analysis.status) ? false : 2000,
  });

export function useAcceptAnalysis(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AcceptAnalysisRequest) =>
      api<{ exerciseId: string }>(`/analyses/${id}/accept`, { method: 'POST', body }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.exercises });
      await qc.invalidateQueries({ queryKey: keys.analysis(id) });
    },
  });
}

export const discardAnalysis = (id: string) =>
  api<void>(`/analyses/${id}/discard`, { method: 'POST' });

// --- Trainingen (S3) -------------------------------------------------------------

/** mine = eigen trainingen; club = gedeeld door clubgenoten in de actieve club (D-037). */
export const useTrainingList = (scope: TrainingScope = 'mine') =>
  useInfiniteQuery({
    queryKey: keys.trainingListScope(scope),
    queryFn: ({ pageParam }) =>
      api<TrainingListResponse>(
        `/trainings?scope=${scope}${pageParam ? `&cursor=${pageParam}` : ''}`,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export const useTraining = (id: string) =>
  useQuery({
    queryKey: keys.training(id),
    queryFn: () => api<TrainingDetailResponse>(`/trainings/${id}`),
    enabled: id !== '',
  });

/**
 * Na elke wijziging: de training bijwerken en lijsten verversen. Ook de
 * oefeningen, want toevoegen kan een eigen concept vergrendelen (D-033).
 */
function useTrainingMutation<TVars, TData extends TrainingDetailResponse | void>(
  fn: (vars: TVars) => Promise<TData>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (data) => {
      if (data) qc.setQueryData(keys.training(data.training.id), data);
      await qc.invalidateQueries({ queryKey: keys.trainingList });
      await qc.invalidateQueries({ queryKey: keys.exercises });
    },
  });
}

export const useCreateTraining = () =>
  useTrainingMutation((body: TrainingInput) =>
    api<TrainingDetailResponse>('/trainings', { method: 'POST', body }),
  );

export const useUpdateTraining = (id: string) =>
  useTrainingMutation((body: TrainingInput) =>
    api<TrainingDetailResponse>(`/trainings/${id}`, { method: 'PUT', body }),
  );

export const useDeleteTraining = (id: string) =>
  useTrainingMutation(() => api<void>(`/trainings/${id}`, { method: 'DELETE' }));

export const useDuplicateTraining = (id: string) =>
  useTrainingMutation(() =>
    api<TrainingDetailResponse>(`/trainings/${id}/duplicate`, { method: 'POST' }),
  );

export const useUpdateTrainingItemVersion = (id: string) =>
  useTrainingMutation((itemId: string) =>
    api<TrainingDetailResponse>(`/trainings/${id}/items/${itemId}/update-version`, {
      method: 'POST',
    }),
  );

/**
 * Oefening onderaan een bestaande training: de huidige regels ongewijzigd
 * meesturen (ze houden hun versie), plus één nieuwe regel.
 */
export const useAddExerciseToTraining = (exerciseId: string) =>
  useTrainingMutation(async (trainingId: string) => {
    const { training: t } = await api<TrainingDetailResponse>(`/trainings/${trainingId}`);
    const body: TrainingInput = {
      title: t.title,
      description: t.description,
      scheduledAt: t.scheduledAt,
      teamName: t.teamName,
      playerCount: t.playerCount,
      courtCount: t.courtCount,
      targetDurationMinutes: t.targetDurationMinutes,
      shareWithClub: t.shared,
      items: [
        ...t.items.map((i) => ({
          id: i.id,
          exerciseId: i.exerciseId,
          plannedDurationMinutes: i.plannedDurationMinutes,
          trainerNotes: i.trainerNotes,
        })),
        { exerciseId },
      ],
    };
    return api<TrainingDetailResponse>(`/trainings/${trainingId}`, { method: 'PUT', body });
  });
